import Button from "@/common/components/Button"
import { ImageUploader } from "@/common/components/ImageUploader"
import MaskedTextField from "@/common/components/MaskedTextField"
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple"
import SelectController from "@/common/components/SelectController"
import TextField from "@/common/components/TextField"
import { cnpjMask, numericRegexMask, phoneMask, stateInscriptionMask } from "@/common/constants/masks.constant"
import { simpleNationalOptantOptions } from "@/common/constants/simpleNationalOptantOptions.constant"
import { useEstablishmentFlags } from "@/common/hooks/queries/useEstablishmentFlags"
import { useVaduCnpjDetails } from "@/common/hooks/queries/useVaduCnpjDetails"
import useDebounce from "@/common/hooks/useDebounce"
import { Mapper } from "@/common/services/mapper"
import { Flag } from "@/common/types/establishment/flag.type"
import { VaduCnpjDetailsResponse } from "@/common/types/vaduCnpjDetails.type"
import { checkIsImage } from "@/common/utils/fileValidator"
import { unFormatCnpj } from "@/common/utils/unFormatters"
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview"
import { ReviewButtons } from "@/features/establishmentLeadsFunnel/components/ReviewButtons"
import { commercialKindOptions } from "@/features/establishmentLeadsFunnel/constants/commercialKindOptions.constant"
import useReviewSectionLead from "@/features/establishmentLeadsFunnel/hooks/useReviewSectionLead"
import { EstablishmentLeadsFunnelUpdateValidationSchema } from "@/features/establishmentLeadsFunnel/schemas/update.schema"
import { EstablishmentLeadData } from "@/features/establishmentLeadsFunnel/types/establishmentLead.type"
import { useIsMutating } from "@tanstack/react-query"
import { BaseSyntheticEvent, Dispatch, SetStateAction, useState } from "react"
import { Control, FieldErrors, useFormContext, UseFormRegister } from "react-hook-form"

interface GeneralInformationSectionProps {
  establishmentLeadId?: string
  general_information?: EstablishmentLeadData['general_information']
  leadState: EstablishmentLeadData['state']
  control: Control<EstablishmentLeadsFunnelUpdateValidationSchema>
  register: UseFormRegister<EstablishmentLeadsFunnelUpdateValidationSchema>
  errors: FieldErrors<EstablishmentLeadsFunnelUpdateValidationSchema>
  submit?: (e?: BaseSyntheticEvent) => Promise<void>
  submittableOnBlur?: (e: React.FocusEvent<HTMLInputElement, Element>, onBlur: (e: React.FocusEvent<HTMLInputElement, Element>) => void) => void
  file?: Blob | undefined
  setFile?: Dispatch<SetStateAction<Blob | undefined>>
  updateLeadOnSuccess?: () => void
  disabled?: boolean
}

export const GeneralInformationSection = ({
  establishmentLeadId,
  general_information,
  leadState,
  control,
  register,
  errors,
  submit,
  submittableOnBlur,
  file,
  setFile,
  updateLeadOnSuccess,
  disabled,
}: GeneralInformationSectionProps) => {
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { setValue, getValues } = useFormContext<EstablishmentLeadsFunnelUpdateValidationSchema>()

  const isMutating = !!useIsMutating()

  const [searchEstablishmentFlags, setSearchEstablishmentFlags] = useDebounce('')

  const {
    data: establishmentFlags
  } = useEstablishmentFlags({
    filters: {
      search: searchEstablishmentFlags,
    },
    mapper: Mapper.mapToOptions<Flag>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const {
    mutate: approveGeneralInformationLead,
    isError: approveGeneralInformationLeadIsError,
  } = useReviewSectionLead({
    establishmentLeadId,
    state: 'approved',
    section: 'general_information_attributes',
    onSuccess: updateLeadOnSuccess,
  })

  const {
    mutate: declineGeneralInformationLead,
    isError: declineGeneralInformationLeadIsError,
  } = useReviewSectionLead({
    establishmentLeadId,
    state: 'disapproved',
    section: 'general_information_attributes',
    onSuccess: updateLeadOnSuccess,
  })

  const isServerError = (approveGeneralInformationLeadIsError || declineGeneralInformationLeadIsError)

  const successGetVaduCnpjDetails = (data: VaduCnpjDetailsResponse) => {
    const { general_information: generalInformation } = data.data.company

    const oldGeneralInformation = getValues('general_information')

    setValue('general_information', {
      ...oldGeneralInformation,
      commercial_kind: general_information?.commercial_kind
        ? commercialKindOptions.find(({ value }) => value === general_information?.commercial_kind) || null
        : null,
      cnpj: generalInformation.cnpj || '',
      fantasy_name: generalInformation.fantasy_name || '',
      official_name: generalInformation.official_name || '',
      email: generalInformation.email || '',
      primary_cnae: unFormatCnpj(generalInformation.primary_cnae || ''),
      website: generalInformation.website || '',
    })

    submit?.()
  }

  const { mutate: getVaduCnpjDetails, isPending: vaduCnpjDetailsIsPending } = useVaduCnpjDetails({
    onSuccess: successGetVaduCnpjDetails,
    onError: (error) => {
      setMessages((error.response?.data as { errors: { base: string[] } })?.errors?.base || null)
    },
  })

  const handleImportCnpjData = () => {
    getVaduCnpjDetails(unFormatCnpj(general_information?.cnpj || ''))
  }

  return (
    <div className="flex flex-col gap-4">
      {isServerError && (
        <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, cheque os campos e tente novamente</p>
      )}
      <div className="flex justify-between">
        <p className="text-[18px] font-bold">Dados Gerais</p>
        {(leadState === 'qualifying_review') && (
          <ReviewButtons
            name="general_information"
            reviewState={general_information?.last_review?.state}
            onApprove={approveGeneralInformationLead}
            onDecline={declineGeneralInformationLead}
            isPending={isMutating}
          />
        )}
      </div>
      {general_information?.last_review?.state === 'disapproved' && (
        <RequestedReview description={general_information?.last_review?.description} />
      )}
      <div className="flex w-full items-end gap-2">
        <div className="flex w-full gap-2">
          <ImageUploader
            isLoading={false}
            label="Foto"
            name="profile-picture"
            accept="image/*"
            onChange={(e) => {
              const file = (e.target as HTMLInputElement & { files: FileList }).files?.[0]
              if (!file || !checkIsImage(file)) {
                return
              }
              setFile && setFile(file)
              submit && submit()
            }}
            currentImageUrl={file ?
              URL.createObjectURL(file) :
              general_information?.company_logo?.url ?
                general_information?.company_logo?.url :
                undefined
            }
            disabled={disabled}
          />

          <div className="flex flex-col w-full items-start gap-y-1">
            <MaskedTextField
              mask={cnpjMask}
              label="CNPJ"
              markAsRequired
              defaultValue={general_information?.cnpj}
              errorMessage={errors?.general_information?.cnpj?.message || undefined}
              {...register('general_information.cnpj')}
              onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.cnpj').onBlur)}
              disabled
              className="w-full mt-[14px]"
            />

            <Button
              variant="link"
              onClick={handleImportCnpjData}
              disabled={
                disabled ||
                !(general_information?.cnpj && general_information?.cnpj?.length >= 14) ||
                vaduCnpjDetailsIsPending
              }
              isLoading={vaduCnpjDetailsIsPending}
            >
              Importar dados
            </Button>
          </div>
        </div>

        <div className="flex flex-col w-full items-start gap-y-1">
          <TextField
            label="Tipo de estabelecimento"
            markAsRequired
            className="w-full"
            {...register('general_information.commercial_kind.label')}
            disabled
          />

          <div className="h-5" />
        </div>
      </div>
      <div className="flex gap-2">
        <TextField
          label="Nome Fantasia"
          markAsRequired
          className="w-1/2"
          maxLength={64}
          defaultValue={general_information?.fantasy_name}
          errorMessage={errors?.general_information?.fantasy_name?.message || undefined}
          {...register('general_information.fantasy_name')}
          onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.fantasy_name').onBlur)}
          disabled={disabled}
        />
        <TextField
          label="Nome Empresarial"
          markAsRequired
          className="w-1/2"
          maxLength={64}
          defaultValue={general_information?.official_name}
          errorMessage={errors?.general_information?.official_name?.message || undefined}
          {...register('general_information.official_name')}
          onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.official_name').onBlur)}
          disabled={disabled}
        />
      </div>
      <div className="flex gap-2">
        <TextField
          label="Endereço eletrônico"
          markAsRequired
          className="w-1/2"
          maxLength={64}
          defaultValue={general_information?.email}
          errorMessage={errors?.general_information?.email?.message || undefined}
          {...register('general_information.email')}
          onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.email').onBlur)}
          disabled={disabled}
        />
        <MaskedTextField
          label="Telefone"
          mask={phoneMask}
          className="w-1/2"
          maxLength={64}
          defaultValue={general_information?.phone?.number}
          errorMessage={errors?.general_information?.phone?.number?.message || undefined}
          {...register('general_information.phone.number')}
          onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.phone.number').onBlur)}
          disabled={disabled}
        />
      </div>
      <SelectController
        label="Optante pelo simples nacional"
        name="general_information.simple_national_optant"
        control={control}
        options={simpleNationalOptantOptions}
        markAsRequired
        triggerOnChange={submit}
        isDisabled={disabled}
        errorMessage={errors?.general_information?.simple_national_optant?.message || undefined}
      />
      <div className="flex gap-2">
        <MaskedTextField
          label="IE (Inscrição Estadual)"
          markAsRequired
          mask={stateInscriptionMask}
          defaultValue={general_information?.state_inscription}
          errorMessage={errors?.general_information?.state_inscription?.message || undefined}
          className="w-1/2"
          {...register('general_information.state_inscription')}
          onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.state_inscription').onBlur)}
          disabled={disabled}
        />
        <MaskedTextField
          label="CNAE"
          mask={numericRegexMask(7)}
          defaultValue={general_information?.primary_cnae}
          errorMessage={errors?.general_information?.primary_cnae?.message || undefined}
          className="w-1/2"
          {...register('general_information.primary_cnae')}
          onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.primary_cnae').onBlur)}
          disabled={disabled}
          markAsRequired
        />
      </div>
      <SelectController
        control={control}
        markAsRequired
        name="general_information.flag"
        label="Bandeira"
        triggerOnChange={submit}
        options={establishmentFlags}
        onInputChange={(value) => setSearchEstablishmentFlags(value)}
        isDisabled={disabled}
      />
      <TextField
        label="Site ou Redes sociais"
        defaultValue={general_information?.website}
        errorMessage={errors?.general_information?.website?.message || undefined}
        {...register('general_information.website')}
        onBlur={(e) => submittableOnBlur && submittableOnBlur(e, register('general_information.website').onBlur)}
        disabled={disabled}
      />
      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </div>
  )
}
